import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart3, Users, Eye, MousePointerClick, Globe, Monitor, Smartphone, Tablet,
  TrendingUp, Calendar, RefreshCw, Loader2, ArrowUpRight, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPage({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('30');
  const [summary, setSummary] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [summaryRes, recentRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?days=${period}`),
        axios.get(`${API}/analytics/recent?limit=20`)
      ]);
      setSummary(summaryRes.data);
      setRecentVisitors(recentRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getPageLabel = (page) => {
    const labels = {
      'widget_availability': 'Availability Widget',
      'widget_contact': 'Contact Widget',
      'widget_login': 'Login Widget',
      'inquiry_form': 'Inquiry Form',
      'client_portal': 'Client Portal'
    };
    return labels[page] || page;
  };

  const getEventLabel = (eventType) => {
    const labels = {
      'page_view': 'Page View',
      'widget_view': 'Widget View',
      'form_submit': 'Form Submit',
      'button_click': 'Button Click'
    };
    return labels[eventType] || eventType;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian p-8 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-neon-blue mb-4" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const maxDailyViews = summary?.daily_views?.length > 0 
    ? Math.max(...summary.daily_views.map(d => d.views)) 
    : 1;

  return (
    <div className="min-h-screen bg-obsidian p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-neon-blue" />
              Analytics
            </h1>
            <p className="text-muted-foreground mt-1">
              Track visitor activity on your widgets and public pages
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 bg-card border-glass-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-glass-border">
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-glass-border"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-glass-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.total_views || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-neon-blue/20 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-neon-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-glass-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unique Visitors</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.unique_visitors || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-neon-purple/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-neon-purple" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-glass-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Widget Views</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.widget_views || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-glass-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Form Submits</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.form_submits || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <MousePointerClick className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Daily Views Chart */}
          <Card className="glass-card border-glass-border lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-neon-blue" />
                Views Over Time
              </CardTitle>
              <CardDescription>Daily visitor activity</CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.daily_views?.length > 0 ? (
                <div className="h-64">
                  <div className="flex items-end justify-between h-48 gap-1">
                    {summary.daily_views.slice(-30).map((day, idx) => (
                      <div
                        key={day.date}
                        className="flex-1 flex flex-col items-center group"
                      >
                        <div className="relative w-full">
                          <div
                            className="w-full bg-neon-blue/80 hover:bg-neon-blue rounded-t transition-all cursor-pointer"
                            style={{ 
                              height: `${(day.views / maxDailyViews) * 180}px`,
                              minHeight: day.views > 0 ? '4px' : '0px'
                            }}
                          />
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-card border border-glass-border rounded px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            {day.views} views
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <span>{formatDate(summary.daily_views[0]?.date)}</span>
                    <span>{formatDate(summary.daily_views[summary.daily_views.length - 1]?.date)}</span>
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No data yet</p>
                    <p className="text-sm">Views will appear once visitors start using your widgets</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Device Breakdown */}
          <Card className="glass-card border-glass-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-neon-purple" />
                Devices
              </CardTitle>
              <CardDescription>Visitor device types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { key: 'desktop', label: 'Desktop', icon: Monitor, color: 'text-blue-500' },
                  { key: 'mobile', label: 'Mobile', icon: Smartphone, color: 'text-green-500' },
                  { key: 'tablet', label: 'Tablet', icon: Tablet, color: 'text-purple-500' }
                ].map(device => {
                  const count = summary?.devices?.[device.key] || 0;
                  const total = (summary?.devices?.desktop || 0) + (summary?.devices?.mobile || 0) + (summary?.devices?.tablet || 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  
                  return (
                    <div key={device.key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <device.icon className={`w-4 h-4 ${device.color}`} />
                          <span className="text-sm text-foreground">{device.label}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            device.key === 'desktop' ? 'bg-blue-500' :
                            device.key === 'mobile' ? 'bg-green-500' : 'bg-purple-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Referrers */}
          <Card className="glass-card border-glass-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-green-500" />
                Top Referrers
              </CardTitle>
              <CardDescription>Where your visitors come from</CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.top_referrers?.length > 0 ? (
                <div className="space-y-3">
                  {summary.top_referrers.slice(0, 8).map((ref, idx) => (
                    <div key={ref.referrer} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-4">{idx + 1}.</span>
                        <span className="text-sm text-foreground truncate max-w-[200px]">
                          {ref.referrer}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-neon-blue">{ref.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No referrer data yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Page Breakdown */}
          <Card className="glass-card border-glass-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-yellow-500" />
                Popular Pages
              </CardTitle>
              <CardDescription>Views by page/widget</CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.page_breakdown && Object.keys(summary.page_breakdown).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(summary.page_breakdown)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 8)
                    .map(([page, count], idx) => (
                      <div key={page} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground w-4">{idx + 1}.</span>
                          <span className="text-sm text-foreground">{getPageLabel(page)}</span>
                        </div>
                        <span className="text-sm font-medium text-yellow-500">{count}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No page data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="glass-card border-glass-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-neon-blue" />
              Recent Activity
            </CardTitle>
            <CardDescription>Latest visitor interactions</CardDescription>
          </CardHeader>
          <CardContent>
            {recentVisitors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-glass-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Time</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Event</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Page</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Referrer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentVisitors.map((visitor, idx) => (
                      <tr key={visitor.id || idx} className="border-b border-glass-border/50 hover:bg-muted/10">
                        <td className="py-3 px-4 text-sm text-foreground">
                          {formatTimestamp(visitor.timestamp)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            visitor.event_type === 'form_submit' 
                              ? 'bg-green-500/20 text-green-500'
                              : visitor.event_type === 'widget_view'
                              ? 'bg-neon-blue/20 text-neon-blue'
                              : 'bg-muted/50 text-muted-foreground'
                          }`}>
                            {getEventLabel(visitor.event_type)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-foreground">
                          {getPageLabel(visitor.page)}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground truncate max-w-[150px]">
                          {visitor.referrer || 'Direct'}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground font-mono">
                          {visitor.ip_address}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No visitor activity yet</p>
                <p className="text-sm mt-2">Once visitors use your widgets, their activity will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
